import clsx from 'clsx'
import dayjs from 'dayjs'
import { CaretLeft, CaretRight } from 'phosphor-react'
import { useEffect, useMemo, useState } from 'react'
import { capitalize } from '../../utils/capitalize'

type CalendarWeek = {
  week: number
  days: Array<{
    date: dayjs.Dayjs
    disabled: boolean
  }>
}

type CalendarWeeks = CalendarWeek[]

type CalendarProps = {
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
}

const getWeekDays = (year: number, month: number) => {
  const formatter = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' })

  return Array.from(Array(7).keys())
    .map((day) => {
      return formatter.format(new Date(Date.UTC(year, month, day)))
    })
    .map((weekday) => {
      return weekday.substring(0, 1).toUpperCase()
    })
}

export const Calendar = ({ selectedDate, onDateSelect }: CalendarProps) => {
  const [currentDate, setCurrentDate] = useState(() => {
    return dayjs(selectedDate).set('date', 1)
  })

  const calendarWeeks = useMemo(() => {
    const daysOfMonth = Array.from({ length: currentDate.daysInMonth() }).map(
      (_, i) => {
        return currentDate.set('date', i + 1)
      }
    )

    const firstDayOfMonthInWeek = currentDate.get('day')
    const daysOfPreviousMonthInWeek = Array.from({
      length: firstDayOfMonthInWeek,
    })
      .map((_, i) => {
        return currentDate.subtract(i + 1, 'day')
      })
      .reverse()

    const lastDayInCurrentMonth = currentDate.set(
      'date',
      currentDate.daysInMonth()
    )
    const lastWeekDay = lastDayInCurrentMonth.get('day')

    const daysOfNextMonthWeek = Array.from({
      length: 7 - (lastWeekDay + 1),
    }).map((_, i) => {
      return lastDayInCurrentMonth.add(i + 1, 'day')
    })

    const calendarDays = [
      ...daysOfPreviousMonthInWeek.map((date) => {
        return { date, disabled: true }
      }),
      ...daysOfMonth.map((date) => {
        return { date, disabled: date.endOf('day').isBefore(new Date()) }
      }),
      ...daysOfNextMonthWeek.map((date) => {
        return { date, disabled: true }
      }),
    ]

    const calendarWeeks = calendarDays.reduce<CalendarWeeks>(
      (acc, _, i, original) => {
        const isNewWeek = i % 7 === 0

        if (isNewWeek) {
          acc.push({
            week: i / 7 + 1,
            days: original.slice(i, i + 7),
          })
        }

        return acc
      },
      []
    )

    return calendarWeeks
  }, [currentDate])

  useEffect(() => {
    setCurrentDate(dayjs(selectedDate).set('date', 1))
  }, [selectedDate])

  const now = selectedDate || new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  const weekdays = getWeekDays(currentYear, currentMonth)
  const currentMonthInText = capitalize(currentDate.format('MMMM'))
  const currentYearInText = currentDate.format('YYYY')

  const handlePreviousMonth = () => {
    const previousMonth = currentDate.subtract(1, 'month')
    setCurrentDate(previousMonth)
  }

  const handleNextMonth = () => {
    const nextMonth = currentDate.add(1, 'month')
    setCurrentDate(nextMonth)
  }

  return (
    <div className="mb-10">
      <div className="px-4 border border-primary-light py-7 rounded-[10px]">
        <header className="flex items-center justify-between mb-6">
          <button type="button" onClick={handlePreviousMonth}>
            <CaretLeft color="#203760" weight="bold" />
          </button>
          <span className="text-sm font-bold font-roboto text-grey-700">
            {currentMonthInText} de {currentYearInText}
          </span>
          <button type="button" onClick={handleNextMonth}>
            <CaretRight color="#203760" weight="bold" />
          </button>
        </header>

        <div>
          <div className="flex items-center justify-between mb-3">
            {weekdays.map((weekday, index) => (
              <span
                key={index}
                className="w-[34px] h-[24px] flex items-center justify-center font-normal font-roboto text-[#203760] text-xs"
              >
                {weekday}
              </span>
            ))}
          </div>

          <div className="space-y-3">
            {calendarWeeks.map(({ week, days }) => (
              <div key={week} className="flex items-center justify-between">
                {days.map(({ date, disabled }, index) => {
                  const isSameDate = date.isSame(selectedDate)

                  return (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onDateSelect(date.toDate())}
                      key={date.toString()}
                      className={clsx(
                        'w-[34px] h-[24px] rounded-lg font-medium flex items-center justify-center font-roboto text-sm',
                        {
                          'text-grey-700 cursor-not-allowed': disabled,
                          'text-[#333]': !disabled && !isSameDate,
                          'bg-primary-light text-white': isSameDate,
                        }
                      )}
                    >
                      {date.get('date')}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
